// import {StrictMode} from "react"; // TODO: Re-enable after fixing the legacy modal
import {Buffer} from "buffer";
import React, {lazy, Suspense} from "react";
import {createRoot} from "react-dom/client";
import {Provider} from "react-redux";
import {store} from "./redux";
import "./style/index.scss";
import "./utils/apiConfig"; // Initialize API configuration
import "./utils/consoleCapture"; // Console capture utility
import {initClipboardPolyfill} from "./utils/clipboardPolyfill"; // Clipboard polyfill for Overwolf/iframe contexts
import {installChunkLoadRetry} from "./utils/chunkLoadRetry"; // Auto-reload on stale-deploy chunk-load failure
import CustomHubShell from "@/CustomHubShell";
import AppBootFallback from "@/components/common/AppBootFallback";
import {CUSTOM_HUB_REGISTRY, findRegisteredCustomHubSlug} from "@/customHubs/registry";

/*
 * Expose Buffer on the global so Web3 deps (bn.js, elliptic, etc.) that
 * reference `window.Buffer` / `globalThis.Buffer` at module-load time find
 * a real implementation. Without this, Vite's externalized-for-browser
 * stub logs `Module "buffer" has been externalized` twice per page load.
 */
declare global {
    interface Window {
        Buffer: typeof Buffer;
    }
}
if (typeof window !== "undefined" && !window.Buffer) {
    window.Buffer = Buffer;
}

// Initialize clipboard polyfill before React loads (catches third-party library clipboard calls)
initClipboardPolyfill();

/*
 * Wire chunk-load failure → forced reload BEFORE any lazy chunk can fail. The
 * lazy `PlatformBranch` import + every `lazy()` route below can hit a stale
 * cached index.html referencing chunk hashes that no longer exist after a
 * deploy. Without this handler React surfaces the rejection as a blank page.
 */
installChunkLoadRetry();

/*
 * Dark, always. The light/dark control was removed from the app years ago and
 * `useThemePreference` force-writes DARK regardless of what is passed to it —
 * this was the last reader of the stored preference, so a `persist:meta` blob
 * predating that change could still hand a returning member the stylesheet's
 * LIGHT element resets.
 */
document.documentElement.setAttribute("data-theme", "dark");

/*
 * Path-based root fork — partner-branded campaigns (RTTR, upcoming
 * Polaris × CoD) mount the minimal CustomHubShell provider stack
 * instead of the full platform DevourProviders. Saves ~200-500ms of
 * provider startup work and enables independent code chunking per
 * campaign.
 *
 * The list of registered partner campaigns lives in
 * `customHubs/registry.ts` — adding a new campaign there auto-extends
 * this fork. No edits needed here per campaign.
 *
 * Fork happens BEFORE React Router mounts because the providers live
 * above the router. Constraint: navigation away from a partner route
 * requires a hard page reload to re-enter this fork. Acceptable for
 * partner-isolated routes that have no outbound internal links (see
 * CustomHubShell.tsx for the full rationale + allow-list).
 */
const matchedCustomHubSlug = findRegisteredCustomHubSlug(window.location.pathname);
const matchedCustomHubBrand = matchedCustomHubSlug
    ? CUSTOM_HUB_REGISTRY[matchedCustomHubSlug]
    : undefined;

/*
 * Platform branch is lazy-loaded so partner-route WebViews never download
 * `App.tsx` or `DevourProviders` (Statsig SDK, Websocket, SocialDrawer,
 * Sentry profiler, ElectronUpdateOverlay) — together a ~1.5–2MB chunk of
 * JS that's wholly unused on `/hub/<partner-slug>` pages.
 *
 * When matchedCustomHubBrand is set, the `lazy()` reference below is
 * never rendered → Vite/Rollup never fetches the chunk → the partner
 * WebView downloads ONLY main.tsx + CustomHubShell + shared vendor +
 * the per-slug hub chunk. DevourPlay platform routes pay one extra
 * HTTP round-trip on first paint to load this lazy chunk; for partner
 * embeds the savings are dramatic.
 */
const PlatformBranch = matchedCustomHubBrand
    ? null
    : lazy(async() => {
        const [providersMod, appMod] = await Promise.all([
            import("@/DevourProviders"),
            import("./App"),
        ]);
        const Providers = providersMod.default;
        const App = appMod.default;
        return {
            default: () =>
                <Providers>
                    <App />
                </Providers>,
        };
    });

// TODO: Re-enable StrictMode after fixing the legacy modal cleanup handling
createRoot(document.getElementById("root")).render(<Provider store={store}>
    {matchedCustomHubBrand
        ? <CustomHubShell brand={matchedCustomHubBrand} />
        /*
         * fallback was `null`, which painted NOTHING while the lazy platform
         * chunk was in flight — React had already replaced index.html's boot
         * loader, so a slow or stalled chunk left the user on a bare background.
         * In the Electron shell (frame: false) that is the reported black screen.
         */
        : <Suspense fallback={<AppBootFallback />}>
            {PlatformBranch ? <PlatformBranch /> : null}
        </Suspense>
    }
</Provider>);

/*
 * Post-mount diagnostics — moved off the synchronous main.tsx critical
 * path so the Sentry SDK and Overwolf network debugger don't block first
 * paint. Both are observability-only; not load-bearing for rendering.
 *
 *   - Sentry: still loaded on every route (errors matter everywhere) but
 *     via dynamic import so its ~150-300KB SDK doesn't enter the main
 *     bundle. Init runs once the lazy chunk resolves.
 *   - Network debugger: only meaningful inside the Overwolf desktop
 *     overlay (its job is logging Overwolf-specific fetch quirks). On a
 *     partner Realm WebView there's no Overwolf shell to inspect, so we
 *     skip the import entirely — saves both the JS payload and the
 *     unused setInterval/console-hook side effects.
 */
import("./utils/sentry").then((m) => m.initSentry());
/*
 * Memory-growth breadcrumbs piggyback on Sentry. When an OOM crash fires
 * (DEVOURPLAY-ELECTRON-A in the Electron renderer), the SDK auto-attaches
 * the most-recent breadcrumbs to the event — giving us a usedJsHeap
 * timeline at the moment of crash. Zero new infra, zero quota burn.
 */
void import("./utils/memoryTelemetry").then((m) => m.startMemoryTelemetry());
if (!matchedCustomHubBrand) {
    void import("./utils/debugNetworkRequests").then((m) => m.initializeNetworkDebugger());
}


